import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, ArrowLeft } from "lucide-react";
import {
  getRecipes,
  createRecipe,
  updateRecipe,
  deleteRecipe,
} from "@/lib/api";
import { CUISINE_ORDER, metaFor } from "@/lib/cuisines";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const EMPTY = {
  name: "",
  cuisine: "indian",
  image: "",
  description: "",
  origin: "",
  cook_time: "",
  difficulty: "Easy",
  spices: "",
  ingredients: "",
  steps: "",
  veg_alternative: "",
  vegan_alternative: "",
};

const toForm = (r) => ({
  name: r.name,
  cuisine: r.cuisine,
  image: r.image,
  description: r.description,
  origin: r.origin,
  cook_time: r.cook_time,
  difficulty: r.difficulty,
  spices: r.spices.map((s) => `${s.name} | ${s.role}`).join("\n"),
  ingredients: r.ingredients.join("\n"),
  steps: r.steps.join("\n"),
  veg_alternative: r.veg_alternative || "",
  vegan_alternative: r.vegan_alternative || "",
});

const toPayload = (f) => ({
  name: f.name.trim(),
  cuisine: f.cuisine,
  image: f.image.trim(),
  description: f.description.trim(),
  origin: f.origin.trim(),
  cook_time: f.cook_time.trim(),
  difficulty: f.difficulty,
  veg_alternative: f.veg_alternative.trim(),
  vegan_alternative: f.vegan_alternative.trim(),
  spices: f.spices
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const [name, ...rest] = l.split("|");
      return { name: name.trim(), role: rest.join("|").trim() };
    }),
  ingredients: f.ingredients.split("\n").map((l) => l.trim()).filter(Boolean),
  steps: f.steps.split("\n").map((l) => l.trim()).filter(Boolean),
});

export default function AdminPage() {
  const [recipes, setRecipes] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => getRecipes().then(setRecipes);
  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const startAdd = () => {
    setForm(EMPTY);
    setEditingId(null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startEdit = (r) => {
    setForm(toForm(r));
    setEditingId(r.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.image.trim() || !form.cook_time.trim()) {
      toast.error("Name, image URL and cook time are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = toPayload(form);
      if (editingId) {
        await updateRecipe(editingId, payload);
        toast.success("Recipe updated");
      } else {
        await createRecipe(payload);
        toast.success("Recipe created");
      }
      setShowForm(false);
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error("Something went wrong. Check the fields and try again.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteRecipe(deleteTarget.id);
      toast.success("Recipe deleted");
      setDeleteTarget(null);
      load();
    } catch {
      toast.error("Could not delete recipe");
    }
  };

  return (
    <div data-testid="admin-page" className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 mb-8"
      >
        <ArrowLeft className="h-4 w-4" /> Back to site
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-gray-500 mb-3">
            Control Room
          </p>
          <h1 className="font-heading text-5xl sm:text-6xl font-light tracking-tighter text-gray-900">
            Manage Recipes
          </h1>
        </div>
        {!showForm && (
          <Button
            onClick={startAdd}
            data-testid="admin-add-recipe-button"
            className="rounded-none bg-gray-900 hover:bg-gray-700 gap-2"
          >
            <Plus className="h-4 w-4" /> Add Recipe
          </Button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <form
          onSubmit={submit}
          data-testid="admin-recipe-form"
          className="border border-gray-900 p-6 md:p-10 mb-14"
        >
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading text-3xl">
              {editingId ? "Edit Recipe" : "New Recipe"}
            </h2>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              data-testid="admin-close-form-button"
              className="p-2 text-gray-500 hover:text-gray-900"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label>Dish name</Label>
              <Input
                data-testid="admin-input-name"
                value={form.name}
                onChange={set("name")}
                className="rounded-none mt-1.5"
                placeholder="e.g. Aloo Jeera"
              />
            </div>
            <div>
              <Label>Cuisine</Label>
              <Select value={form.cuisine} onValueChange={set("cuisine")}>
                <SelectTrigger data-testid="admin-select-cuisine" className="rounded-none mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CUISINE_ORDER.map((k) => (
                    <SelectItem key={k} value={k}>
                      {metaFor(k).name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Image URL</Label>
              <Input
                data-testid="admin-input-image"
                value={form.image}
                onChange={set("image")}
                className="rounded-none mt-1.5"
                placeholder="https://..."
              />
            </div>
            <div>
              <Label>Cook time</Label>
              <Input
                data-testid="admin-input-cooktime"
                value={form.cook_time}
                onChange={set("cook_time")}
                className="rounded-none mt-1.5"
                placeholder="e.g. 20 min"
              />
            </div>
            <div>
              <Label>Difficulty</Label>
              <Select value={form.difficulty} onValueChange={set("difficulty")}>
                <SelectTrigger data-testid="admin-select-difficulty" className="rounded-none mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Easy">Easy</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Hard">Hard</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Short description</Label>
              <Input
                data-testid="admin-input-description"
                value={form.description}
                onChange={set("description")}
                className="rounded-none mt-1.5"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Origin / story</Label>
              <Textarea
                data-testid="admin-input-origin"
                value={form.origin}
                onChange={set("origin")}
                className="rounded-none mt-1.5 min-h-[90px]"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Spices (one per line, format: Name | Role)</Label>
              <Textarea
                data-testid="admin-input-spices"
                value={form.spices}
                onChange={set("spices")}
                className="rounded-none mt-1.5 min-h-[90px] font-mono text-sm"
                placeholder={"Cumin seeds | Toasted in oil for a nutty aroma\nTurmeric | Adds golden colour"}
              />
            </div>
            <div>
              <Label>Ingredients (one per line)</Label>
              <Textarea
                data-testid="admin-input-ingredients"
                value={form.ingredients}
                onChange={set("ingredients")}
                className="rounded-none mt-1.5 min-h-[140px]"
              />
            </div>
            <div>
              <Label>Steps (one per line)</Label>
              <Textarea
                data-testid="admin-input-steps"
                value={form.steps}
                onChange={set("steps")}
                className="rounded-none mt-1.5 min-h-[140px]"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Vegetarian alternative</Label>
              <Textarea
                data-testid="admin-input-veg"
                value={form.veg_alternative}
                onChange={set("veg_alternative")}
                className="rounded-none mt-1.5 min-h-[70px]"
                placeholder="How to make (or note it's already) vegetarian"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Vegan alternative</Label>
              <Textarea
                data-testid="admin-input-vegan"
                value={form.vegan_alternative}
                onChange={set("vegan_alternative")}
                className="rounded-none mt-1.5 min-h-[70px]"
                placeholder="How to make (or note it's already) vegan"
              />
            </div>
          </div>

          <div className="mt-8 flex gap-3">
            <Button
              type="submit"
              disabled={saving}
              data-testid="admin-submit-recipe-button"
              className="rounded-none bg-gray-900 hover:bg-gray-700"
            >
              {saving ? "Saving..." : editingId ? "Update Recipe" : "Create Recipe"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowForm(false)}
              className="rounded-none"
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      {/* Table */}
      <div className="border border-gray-200" data-testid="admin-recipe-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16"></TableHead>
              <TableHead>Recipe</TableHead>
              <TableHead className="hidden md:table-cell">Cuisine</TableHead>
              <TableHead className="hidden md:table-cell">Time</TableHead>
              <TableHead className="hidden md:table-cell">Difficulty</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {recipes.map((r) => (
              <TableRow key={r.id} data-testid={`admin-row-${r.id}`}>
                <TableCell>
                  <img src={r.image} alt="" className="h-10 w-10 object-cover" />
                </TableCell>
                <TableCell className="font-medium text-gray-900">{r.name}</TableCell>
                <TableCell className="hidden md:table-cell capitalize text-gray-600">
                  {r.cuisine}
                </TableCell>
                <TableCell className="hidden md:table-cell text-gray-600">{r.cook_time}</TableCell>
                <TableCell className="hidden md:table-cell text-gray-600">{r.difficulty}</TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => startEdit(r)}
                      data-testid={`admin-edit-${r.id}`}
                      className="rounded-none h-9 w-9"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteTarget(r)}
                      data-testid={`admin-delete-${r.id}`}
                      className="rounded-none h-9 w-9 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="admin-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleteTarget?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the recipe from the cookbook.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="admin-confirm-delete-button"
              className="rounded-none bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
